import { useEffect, useState, useCallback } from "react";
import { Star, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHead, TableSkeleton, StatusBadge, Pager } from "./ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PS = 20;

export default function AdminProviders() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("all");
  const [status, setStatus] = useState("all");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams({ page, page_size: PS });
    if (search) p.set("search", search);
    if (city !== "all") p.set("city", city);
    if (status !== "all") p.set("status", status);
    setData(null);
    api.get(`/admin/providers?${p}`).then((r) => setData(r.data));
  }, [page, search, city, status]);

  useEffect(() => { load(); }, [load]);

  const feature = async (p) => {
    await api.patch(`/admin/providers/${p.id}/feature`, { featured: !p.featured });
    toast.success(`${p.name} ${!p.featured ? "featured" : "unfeatured"}`);
    load();
  };
  const setProviderStatus = async (p, s) => {
    await api.patch(`/admin/providers/${p.id}/status`, { status: s });
    toast.success(`${p.name} → ${s}`);
    load();
  };
  const openDetail = async (id) => {
    setDetail({ loading: true });
    const r = await api.get(`/admin/providers/${id}`);
    setDetail(r.data);
  };

  return (
    <div data-testid="admin-providers">
      <PageHead title="Providers" subtitle="Manage partner hospitals" />
      <div className="flex flex-wrap gap-3 mb-4">
        <Input placeholder="Search by name…" value={search} data-testid="provider-search"
          onChange={(e) => { setPage(1); setSearch(e.target.value); }} className="max-w-xs" />
        <Select value={city} onValueChange={(v) => { setPage(1); setCity(v); }}>
          <SelectTrigger className="w-44" data-testid="provider-city-filter"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All cities</SelectItem>
            {(data?.cities || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}>
          <SelectTrigger className="w-40" data-testid="provider-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            {["all", "VERIFIED", "PENDING", "SUSPENDED", "DELETED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl bg-white p-4 shadow-sm border border-black/5">
        {!data ? <TableSkeleton /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>City</TableHead><TableHead>Specialties</TableHead>
                <TableHead>Rating</TableHead><TableHead>Bookings</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.providers.map((p) => (
                  <TableRow key={p.id} data-testid={`provider-row-${p.id}`}>
                    <TableCell className="font-medium">
                      {p.featured && <Star size={13} className="inline mr-1 text-[#F97316] fill-[#F97316]" />}{p.name}
                    </TableCell>
                    <TableCell>{p.city}</TableCell>
                    <TableCell>{p.specialty_count}</TableCell>
                    <TableCell>{p.rating}</TableCell>
                    <TableCell>{p.booking_count}</TableCell>
                    <TableCell><StatusBadge status={p.accreditation_state || "VERIFIED"} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openDetail(p.id)} data-testid={`provider-view-${p.id}`}><Eye size={15} /></Button>
                      <Button size="sm" variant="outline" className="ml-1" onClick={() => feature(p)} data-testid={`provider-feature-${p.id}`}>{p.featured ? "Unfeature" : "Feature"}</Button>
                      <Button size="sm" variant="outline" className="ml-1"
                        onClick={() => setProviderStatus(p, p.accreditation_state === "SUSPENDED" ? "VERIFIED" : "SUSPENDED")}
                        data-testid={`provider-suspend-${p.id}`}>{p.accreditation_state === "SUSPENDED" ? "Restore" : "Suspend"}</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager page={page} total={data.total} pageSize={PS} onPage={setPage} />
          </div>
        )}
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="provider-detail-panel">
          <SheetHeader><SheetTitle>{detail?.provider?.name || "Provider"}</SheetTitle></SheetHeader>
          {detail && !detail.loading && (
            <div className="mt-4 space-y-4 text-sm">
              {detail.provider.image && <img src={detail.provider.image} alt="" className="w-full h-40 object-cover rounded-xl" />}
              <div className="grid grid-cols-2 gap-3">
                <Stat label="City" value={detail.provider.city} />
                <Stat label="Rating" value={detail.provider.rating} />
                <Stat label="Bookings" value={detail.booking_count} />
                <Stat label="Revenue" value={`$${Number(detail.revenue).toLocaleString()}`} />
              </div>
              <div><div className="font-semibold text-[#111827] mb-1">Specialties</div>
                <div className="text-muted-foreground">{(detail.provider.specialties || []).join(", ")}</div></div>
              <div><div className="font-semibold text-[#111827] mb-1">Facilities</div>
                <div className="text-muted-foreground">{(detail.provider.facilities || []).join(", ")}</div></div>
              <div><div className="font-semibold text-[#111827] mb-1">Recent bookings</div>
                {(detail.bookings || []).slice(0, 8).map((b) => (
                  <div key={b.id} className="flex justify-between py-1 border-b border-black/5">
                    <span className="truncate">{b.package?.name}</span>
                    <span className="text-muted-foreground">${Number(b.split?.tcv || 0).toLocaleString()}</span>
                  </div>
                ))}
                {(!detail.bookings || detail.bookings.length === 0) && <span className="text-muted-foreground">No bookings yet.</span>}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-gray-50 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-semibold text-[#111827]">{value}</div>
    </div>
  );
}
